import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import { getSocket } from '../services/socket';
import {
  CheckCircle, Clock, AlertCircle, ChevronRight, Phone,
  Calendar, Pill, Activity, MessageSquare, X, Loader2
} from 'lucide-react';

/**
 * Patient Action Responses
 * Shows pending actions from care team and allows patient to respond
 * Uses WebSocket for critical updates + polling for general updates
 */
const PatientActionResponses = () => {
  const { user } = useAuth();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);
  const [confirmedId, setConfirmedId] = useState(null); // shows inline confirmation before card removes
  const [confirmedText, setConfirmedText] = useState('');
  const [responseText, setResponseText] = useState('');
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('polling'); // 'realtime', 'polling', 'error'
  const connectionStatusRef = useRef('polling'); // ref so the poll interval reads the live value
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    loadActions();
    
    // Setup WebSocket for real-time critical notifications
    const setupWebSocket = () => {
      try {
        const socket = getSocket();
        
        if (socket) {
          // Listen for new actions from care team
          socket.on('newPatientAction', (data) => {
            if (data.patientId === user?.id) {
              toast.info('New action from your care team', {
                icon: '🔔',
                duration: 5000
              });
              loadActions(); // Refresh list
            }
          });

          // Listen for urgent alerts
          socket.on('urgentPatientAlert', (data) => {
            if (data.patientId === user?.id) {
              toast.error(data.message || 'Urgent: Please contact your care team', {
                duration: 10000,
                icon: '🚨'
              });
              loadActions();
            }
          });

          // Connection status
          socket.on('connect', () => {
            connectionStatusRef.current = 'realtime';
            setConnectionStatus('realtime');
          });

          socket.on('disconnect', () => {
            connectionStatusRef.current = 'polling';
            setConnectionStatus('polling');
          });

          connectionStatusRef.current = 'realtime';
          setConnectionStatus('realtime');
          return socket;
        }
      } catch (err) {
        console.error('WebSocket setup failed:', err);
        setConnectionStatus('error');
      }
      return null;
    };

    const socket = setupWebSocket();

    // Fallback polling: 30s for general updates (WebSocket handles critical)
    const pollInterval = setInterval(() => {
      if (connectionStatusRef.current !== 'realtime') {
        loadActions();
      }
    }, 30000);

    return () => {
      clearInterval(pollInterval);
      if (socket) {
        socket.off('newPatientAction');
        socket.off('urgentPatientAlert');
        socket.off('connect');
        socket.off('disconnect');
      }
    };
  }, [user?.id]);

  const loadActions = async () => {
    try {
      const res = await api.get('/care-events/notifications?limit=20');
      if (res.data?.data) {
        const actionItems = res.data.data.filter(item =>
          item.type === 'action' ||
          (item.type === 'event' && item.actionRequired)
        );
        setActions(actionItems);
        setLoadError(false);
      }
    } catch (err) {
      console.error('Error loading actions:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = (action) => {
    setSelectedAction(action);
    setResponseText('');
    setShowResponseModal(true);
  };

  const removeAfterConfirm = (actionId, text) => {
    setConfirmedId(actionId);
    setConfirmedText(text);
    setTimeout(() => {
      setActions(prev => prev.filter(a => a._id !== actionId));
      setConfirmedId(null);
      setConfirmedText('');
    }, 2000);
  };

  const submitResponse = async () => {
    if (!responseText.trim() || !selectedAction) return;

    setResponding(selectedAction._id);
    try {
      const res = await api.post('/care-events/action-response', {
        actionType: selectedAction.eventType,
        actionId: selectedAction._id,
        patientResponse: responseText,
        timestamp: new Date().toISOString()
      });

      if (res.data?.success) {
        setShowResponseModal(false);
        const text = responseText;
        setSelectedAction(null);
        removeAfterConfirm(selectedAction._id, text);
      }
    } catch (err) {
      toast.error('Failed to submit response');
    } finally {
      setResponding(null);
    }
  };

  const handleQuickResponse = async (action, quickResponse) => {
    setResponding(action._id);
    try {
      const res = await api.post('/care-events/action-response', {
        actionType: action.eventType,
        actionId: action._id,
        patientResponse: quickResponse,
        timestamp: new Date().toISOString()
      });

      if (res.data?.success) {
        removeAfterConfirm(action._id, quickResponse);
      }
    } catch (err) {
      toast.error('Failed to submit response');
    } finally {
      setResponding(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="h-4 bg-slate-100 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="h-20 bg-slate-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-slate-500" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-slate-700">Couldn't load actions</h3>
            <p className="text-sm text-slate-500">Check your connection and try again.</p>
          </div>
          <button
            onClick={loadActions}
            className="text-xs font-bold text-primary hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-bold text-emerald-900">All Caught Up</h3>
            <p className="text-sm text-emerald-700">No pending actions from your care team</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          Actions From Your Care Team
        </h3>
        <div className="flex items-center gap-2">
          {/* Connection status indicator */}
          {connectionStatus === 'realtime' && (
            <span className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold" title="Real-time updates active">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
              Live
            </span>
          )}
          {connectionStatus === 'polling' && (
            <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-bold" title="Checking every 30 seconds">
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
              Synced
            </span>
          )}
          <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
            {actions.length} Pending
          </span>
        </div>
      </div>

      {actions.map((action) => (
        <ActionCard
          key={action._id}
          action={action}
          onRespond={handleRespond}
          onQuickResponse={handleQuickResponse}
          isResponding={responding === action._id}
          isConfirmed={confirmedId === action._id}
          confirmedText={confirmedId === action._id ? confirmedText : ''}
        />
      ))}

      {/* Response Modal */}
      {showResponseModal && selectedAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-slate-900">Your Response</h3>
              <button
                onClick={() => setShowResponseModal(false)}
                className="p-2 text-slate-500 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-600 mb-1">Action from care team:</p>
              <p className="font-bold text-slate-900">{selectedAction.title || selectedAction.eventType}</p>
            </div>

            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Type your response..."
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none h-32"
            />

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowResponseModal(false)}
                className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={submitResponse}
                disabled={!responseText.trim() || responding}
                className="flex-1 px-4 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {responding && <Loader2 className="w-4 h-4 animate-spin" />}
                {responding ? 'Sending…' : 'Submit Response'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual Action Card
const ActionCard = ({ action, onRespond, onQuickResponse, isResponding, isConfirmed, confirmedText }) => {
  if (isConfirmed) {
    return (
      <div className="p-5 rounded-2xl border bg-emerald-50 border-emerald-200 transition-all animate-pulse-once">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <p className="font-bold text-emerald-900">Response recorded</p>
            <p className="text-sm text-emerald-700 mt-0.5">"{confirmedText}"</p>
            <p className="text-xs text-emerald-600 mt-1">Your care team has been notified</p>
          </div>
        </div>
      </div>
    );
  }
  const getIcon = () => {
    const type = action.eventType || '';
    if (type.includes('medication') || type.includes('pharmacy')) return Pill;
    if (type.includes('appointment') || type.includes('schedule')) return Calendar;
    if (type.includes('call') || type.includes('contact')) return Phone;
    if (type.includes('vitals') || type.includes('reading')) return Activity;
    return MessageSquare;
  };

  const Icon = getIcon();
  const severity = action.severity || 'medium';
  
  const severityStyles = {
    critical: 'bg-red-50 border-red-200',
    high: 'bg-amber-50 border-amber-200',
    medium: 'bg-blue-50 border-blue-200',
    low: 'bg-slate-50 border-slate-200'
  };

  const quickResponses = action.actionOptions || [
    'I will do this',
    'I need help with this',
    'I have questions'
  ];

  return (
    <div className={`p-5 rounded-2xl border ${severityStyles[severity]} transition-all hover:shadow-sm`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="font-bold text-slate-900">
                {action.title || 'Action Required'}
              </h4>
              <p className="text-sm text-slate-600 mt-1">
                {action.message || 'Your care team needs you to take action'}
              </p>
            </div>
            <span className={`px-2 py-1 rounded text-xs font-bold ${
              severity === 'critical' ? 'bg-red-100 text-red-700' :
              severity === 'high' ? 'bg-amber-100 text-amber-700' :
              'bg-slate-100 text-slate-600'
            }`}>
              {severity.charAt(0).toUpperCase() + severity.slice(1)}
            </span>
          </div>

          {/* Timestamp */}
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <Clock className="w-3.5 h-3.5" />
            {new Date(action.timestamp || action.createdAt).toLocaleString()}
          </div>

          {/* Quick Response Buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            {quickResponses.map((response) => {
              const isNeedHelp = response === 'I need help with this';
              return (
                <div key={response} className="flex flex-col items-start gap-0.5">
                  <button
                    onClick={() => onQuickResponse(action, response)}
                    disabled={isResponding}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50 ${isNeedHelp ? 'bg-red-50 border border-red-200 text-red-700 hover:bg-red-100' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-primary/30'}`}
                  >
                    {isResponding ? '...' : response}
                  </button>
                  {isNeedHelp && (
                    <span className="text-[10px] text-red-500 font-medium ml-1">
                      Your care team will follow up with you today
                    </span>
                  )}
                </div>
              );
            })}
            <button
              onClick={() => onRespond(action)}
              disabled={isResponding}
              className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-bold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center gap-1"
            >
              {isResponding ? 'Sending...' : <>
                Custom Response <ChevronRight className="w-3 h-3" />
              </>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientActionResponses;
